import type moment from "moment";
import React, { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  coverageChoiceFields,
  selectBookPrivately,
  useCoveringOrganizations,
  useIsCoveredBooking,
} from "../../hooks/useCoveredBooking";
import CoveredCostCenterField from "./CoveredCostCenterField";
import { isFreeOfCharge } from "../../../../../util/payments";
import { RootState } from "../../../../../redux/store";
import {
  addService,
  couponRedemptionLoading,
  editService,
  removeService,
  setOrderComment,
  setTotalPrice,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { formatDate } from "../../../../../util/date";
import { formatCurrency } from "../../../../../util";
import { useFetchVoucher } from "../../../../../redux/flows/fetchVoucherFlow";
import CustomButton from "../../util/CustomButton";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTaxStatement } from "../../../../../util/taxStatement";
import {
  PricePreviewLine,
  PricePreviewResponse,
  useGetPricePreviewQuery,
} from "../../../../../api/pricePreview/pricePreviewApiSlice";
import { requestWindow, resolveRequestDates } from "../../../../../redux/flows/reservationWindow";
import { useSelectedLocationTimeZone } from "../../../../../util/locationTimeZone";
import { spaceOf } from "../../../../../util/useCases";
import type { UseCase } from "../../../../../util/useCases";
import {
  resolveCancellationRule,
  resolveCharging,
  useActiveProductCategory,
} from "../../../../../util/categoryConfig";
import CancellationTermsBlock from "./CancellationTermsBlock";
import { useDirectBookingFlow } from "../../hooks/useDirectBookingFlow";
import { offerStageFor } from "../../../../../util/cancellationRule";

/** The service selection the preview quotes: checked optionals with their
 *  stepper quantity (checkbox services count 1 — the PATCH payload shape). */
const selectedServiceArgs = (
  showAdditionalServices: boolean,
  additionalServices: AdditionalService[],
  checkedServices: Record<string, boolean>,
  quantities: Record<string, number>
) => {
  if (!showAdditionalServices) return [];
  return additionalServices
    .filter(
      (service) =>
        !service.isPaymentCompulsory && checkedServices[service.serviceName]
    )
    .map((service) => ({
      serviceId: service.id,
      quantity: service.quantity ? quantities[service.serviceName] ?? 1 : 1,
    }));
};

/** A failed quote is said out loud, a first load says it is loading — an
 *  empty price box would read as "free" (a failure must never look like an
 *  absence). Booking on regardless stays safe: the server computes the
 *  charge itself at creation and checkout (#2920). */
const PreviewNotice: React.FC<{ failed: boolean; pending: boolean }> = ({
  failed,
  pending,
}) => {
  const { t } = useTranslation();
  if (failed) {
    return (
      <p className="text-sm text-red-600">
        {t("book_location.booking_summary.price_preview_failed")}
      </p>
    );
  }
  if (pending) {
    return (
      <p className="text-sm text-foreground/60">
        {t("book_location.booking_summary.price_loading")}
      </p>
    );
  }
  return null;
};

/**
 * The window the quote is computed for — the SAME window booking creation
 * sends (`reservationWindow.ts`): a venue booking carries its start/end time,
 * a guest-apartment stay its dates. `null` while the flow has not provided it
 * yet (no dates, or a venue time still unset), so nothing is quoted rather
 * than a window that is not the one being booked (#2980 D1).
 */
const quoteWindow = (
  useCase: UseCase,
  orderData: {
    checkInDate?: moment.Moment | null;
    checkOutDate?: moment.Moment | null;
    startTime?: string;
    endTime?: string;
  },
  timeZone: string
): { fromDate: string; toDate: string } | null => {
  if (!orderData.checkInDate || !orderData.checkOutDate) return null;
  try {
    // The same dates creation sends: station midnight of the picked day, on
    // the booked location's clock (#3219).
    const { fromDate, toDate } = resolveRequestDates(orderData, timeZone);
    return requestWindow(
      spaceOf(useCase)?.timeUnit === "hour",
      fromDate,
      toDate,
      orderData.startTime,
      orderData.endTime,
      timeZone
    );
  } catch {
    // setTimeOnDate rejects a venue time that is not set yet ("0").
    return null;
  }
};

/** The query args, or skipToken while the flow has not yet provided the
 *  group and the window (nothing to quote yet). */
const previewQueryArgs = (
  groupId: string | undefined,
  window: { fromDate: string; toDate: string } | null,
  services: { serviceId: string; quantity: number }[],
  coverageChoice: ReturnType<typeof coverageChoiceFields>
) =>
  groupId && window
    ? {
        groupId,
        checkIn: window.fromDate,
        checkOut: window.toDate,
        services,
        ...coverageChoice,
      }
    : skipToken;

/** The server's line list, split for rendering: stay vs services. */
const splitPreviewLines = (preview: PricePreviewResponse | undefined) => {
  const lines = preview?.lines ?? [];
  const serviceLines = lines.filter((line) => line.key === "service");
  return {
    stayLines: lines.filter((line) => line.key !== "service"),
    compulsoryLines: serviceLines.filter((line) => line.compulsory === true),
    additionalServicesTotal: serviceLines
      .filter((line) => line.compulsory !== true)
      .reduce((sum, line) => sum + line.amount, 0),
  };
};

/**
 * ADR-0008 (amendment 2026-10-22): the customer class that priced the quote —
 * set by the server only when a logged-in member's class lowered the price.
 * Shown only when the member pays themselves: a covered booking is quoted at
 * the covering organisation's class (refinement 8) and names who bears the
 * price instead (amendment 2026-09-23).
 */
const pricingClassNameOf = (
  preview: PricePreviewResponse | undefined,
  covered: boolean
): string | null => (covered ? null : preview?.customerClass?.name ?? null);

/**
 * The server's stay-line keys, labelled for display. Count semantics per
 * key: nights (daily), days (weekday), packages/weekend units (weekend),
 * hours (hour), one half-day block (half_day).
 */
const stayLineLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  line: PricePreviewLine
): string => {
  if (line.key === "daily") {
    return line.count + " " + t("apartment.night", { count: line.count });
  }
  return (
    t("book_location.booking_summary." + line.key) + " (" + line.count + "x)"
  );
};

export type AdditionalService = {
  id: string;
  assetId: string;
  serviceName: string;
  unitPrice: number;
  isPaymentCompulsory: boolean;
  quantity: boolean;
  langNames: { [lang: string]: string };
  /** #2873: per_booking | per_hour | per_day; null/absent = per piece. */
  priceUnit?: string | null;
};

interface BookingSummarySharedProps {
  roomImage: string;
  roomTitle: string;
  address: string;
  numberOfGuests: number;
  additionalServices: AdditionalService[];
  showVoucher?: boolean;
  showAdditionalServices?: boolean;
  /** Shapes the window the price preview is quoted for (`quoteWindow`) — the amounts come from the server. */
  useCase: UseCase;
}

const BookingSummaryShared: React.FC<BookingSummarySharedProps> = ({
  roomImage,
  roomTitle,
  address,
  numberOfGuests,
  additionalServices,
  showVoucher = false,
  showAdditionalServices = false,
  useCase,
}) => {
  const { t, i18n } = useTranslation();
  const taxStatement = useTaxStatement();
  const dispatch = useDispatch();
  // Covered by the customer's organization? Answered by the server; this
  // component only renders it (see useCoveredBooking).
  const isCovered = useIsCoveredBooking();
  const coveringOrganizations = useCoveringOrganizations();
  const coveringOrganizationName =
    coveringOrganizations.length === 1 ? coveringOrganizations[0].name : null;

  const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);
  // The same coverage choice the order request sends, so the preview quotes
  // the booking that will be made (refinement 8).
  const bookPrivately = useSelector(selectBookPrivately);
  const coverageChoice = React.useMemo(
    () => coverageChoiceFields(orderData.coveringOrganizationId, bookPrivately),
    [orderData.coveringOrganizationId, bookPrivately]
  );
  const timeZone = useSelectedLocationTimeZone();
  const startDate = orderData.checkInDate;
  const endDate = orderData.checkOutDate;
  const isCouponReedemptionLoading = useSelector(
    (state: RootState) => state.bookingCreation.loading.couponRedemption
  );
  const locationId = useSelector(
    (state: RootState) => state.apartment.selectedApartment?.locationId
  );
  const groupId = useSelector((state: RootState) => state.apartment.selectedApartment?.groupId);
  const voucher = useSelector((state: RootState) => state.bookingCreation.roomRentalVoucher);
  const comment = useSelector(
    (state: RootState) => state.bookingCreation.orderData.comment || ""
  );

  const fetchVoucher = useFetchVoucher();

  const [showAll, setShowAll] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [remarksOpen, setRemarksOpen] = useState(false);

  const [quantities, setQuantities] = useState<Record<string, number>>(
    additionalServices.reduce<Record<string, number>>((acc, service) => {
      acc[service.serviceName] = 1;
      return acc;
    }, {})
  );

  // Sort: non-compulsory first (as before) but within each group by unitPrice desc
  const sortedAdditionalServices = [...additionalServices].sort((a, b) => {
    if (a.isPaymentCompulsory !== b.isPaymentCompulsory) {
      // Previously compulsory went last (true => 1). Keep that behavior
      return a.isPaymentCompulsory ? 1 : -1;
    }
    const aPrice = a.unitPrice || 0;
    const bPrice = b.unitPrice || 0;
    return bPrice - aPrice;
  });

  const displayedServices = showAll
    ? additionalServices
    : sortedAdditionalServices.slice(0, 4);

  const [checkedServices, setCheckedServices] = useState<Record<string, boolean>>(
    displayedServices.reduce<Record<string, boolean>>((acc, service) => {
      acc[service.serviceName] = false;
      return acc;
    }, {})
  );

  // Both maps are seeded from the services known at FIRST render and never
  // re-seeded. After a reload — the guest coming back from Stripe — the
  // apartment's services arrive after that render, so their entries are
  // missing: read through these, never index the maps directly. A missing
  // quantity indexed directly rendered an empty stepper and `undefined + 1`
  // = NaN on "+", and the NaN quantity broke the price preview.
  const quantityOf = (serviceName: string): number => quantities[serviceName] ?? 1;
  const isChecked = (serviceName: string): boolean => checkedServices[serviceName] ?? false;

  const handleCheckboxChange = (serviceName: string) => {
    setCheckedServices((prevState) => {
      const newCheckedState = {
        ...prevState,
        [serviceName]: !prevState[serviceName],
      };

      if (prevState[serviceName]) {
        dispatch(removeService({ name: serviceName }));
      } else {
        dispatch(
          addService({
            serviceId: additionalServices.find((service) => service.serviceName === serviceName)
              ?.id!,
            name: serviceName,
            quantity: quantityOf(serviceName),
          })
        );
      }

      return newCheckedState;
    });
  };

  const handleIncrement = (serviceName: string) => {
    setQuantities((prevQuantities) => {
      const newQuantities = {
        ...prevQuantities,
        [serviceName]: (prevQuantities[serviceName] ?? 1) + 1,
      };
      dispatch(
        editService({
          serviceId: additionalServices.find((service) => service.serviceName === serviceName)?.id!,
          name: serviceName,
          quantity: newQuantities[serviceName],
        })
      );
      return newQuantities;
    });
  };

  const handleDecrement = (serviceName: string) => {
    setQuantities((prevQuantities) => {
      const newQuantities = {
        ...prevQuantities,
        [serviceName]: Math.max(1, (prevQuantities[serviceName] ?? 1) - 1),
      };
      dispatch(
        editService({
          serviceId: additionalServices.find((service) => service.serviceName === serviceName)?.id!,
          name: serviceName,
          quantity: newQuantities[serviceName],
        })
      );
      return newQuantities;
    });
  };

  // #1713 part 3 / #1203: the price this summary shows is the SERVER's own
  // quote — the same number booking creation stores and the checkout PATCH
  // re-derives (#2920). The client computes nothing; it selects and renders.
  const selectedServices = React.useMemo(
    () => selectedServiceArgs(
      showAdditionalServices, additionalServices, checkedServices, quantities
    ),
    [showAdditionalServices, additionalServices, checkedServices, quantities]
  );

  const quotedWindow = quoteWindow(useCase, orderData, timeZone);
  const {
    data: preview,
    isError: previewFailed,
    isFetching: previewFetching,
  } = useGetPricePreviewQuery(
    previewQueryArgs(groupId, quotedWindow, selectedServices, coverageChoice)
  );

  // M10: the cancellation rule of the category being booked — category
  // first, the use case (standard category) as the fallback.
  const categoryKey = useActiveProductCategory();
  const cancellationRule = useSelector((state: RootState) =>
    resolveCancellationRule(state.tenantConfig?.config, useCase, categoryKey)
  );
  const configuredFree = useSelector((state: RootState) =>
    resolveCharging(state.tenantConfig?.config, useCase, categoryKey) === "free"
  );
  // Until when the booking is only an offer (PO, 2026-10-19): a direct
  // booking is paid in the flow; a reservation stays an offer until payment
  // (pay-first) or acceptance (postpay).
  const isDirectBooking = useDirectBookingFlow(useCase);
  const offerUntil = useSelector((state: RootState) =>
    offerStageFor(state.tenantConfig?.config, useCase, categoryKey, isDirectBooking)
  );

  const { stayLines, compulsoryLines, additionalServicesTotal } =
    splitPreviewLines(preview);


  const handleVoucherApply = async () => {
    if (!locationId || !groupId) return;
    dispatch(couponRedemptionLoading({ loading: true }));
    await fetchVoucher(voucherCode, locationId, groupId);
  };
  const isValidVoucher = (code: string) => {
    return /^[a-zA-Z0-9-_ ]+$/.test(code) && code.trim().length > 0;
  };
  const isApplyButtonDisabled = !isValidVoucher(voucherCode);

  let totalPrice = preview?.total ?? 0;
  let reductionPrice = 0;
  if (showVoucher && voucher) {
    if (voucher.discountIsPercentage === true) {
      reductionPrice = totalPrice * (voucher.discountValue / 100);
    } else {
      reductionPrice = voucher.discountValue;
    }
  }
  totalPrice = totalPrice - reductionPrice;

  // The tax line is the non-covered case, and only when there is a price for it
  // to be a statement about. Hoisted out of the JSX so the render body keeps one
  // condition rather than a ternary with a guard nested in its alternate.
  // `stayLines` is the priced breakdown this summary renders. Empty means
  // the quote has not arrived — not that the room is free.
  // The LIST price, before the voucher, and raw (`isFreeOfCharge`): a paid
  // booking a 100 % code brings to 0 is not free — it keeps its tax line and
  // its code field, or the customer could not see or change the code.
  const free = isFreeOfCharge(preview?.total, stayLines.length);
  const showTaxLine = !isCovered && !free;
  const pricingClassName = pricingClassNameOf(preview, isCovered);
  // A voucher cannot reduce a free booking either. This input is gated on the
  // `showVoucher` prop rather than a `showCouponButton`, which is how it was
  // missed the first time round.
  const showVoucherInput = showVoucher && !free;

  useEffect(() => {
    // Only a real server answer feeds the stored total (the PATCH compares
    // it against its own computation, #2920 — a 0 from "not loaded yet"
    // would just trip the deviation warn).
    if (preview) {
      dispatch(setTotalPrice(preview.total));
    }
  }, [dispatch, preview]);

  return (
    <div className="text-foreground max-w-[600px] mx-auto p-4 md:p-6">
      <div className="flex gap-4 mb-4 pb-2">
        <img src={roomImage} alt="Room" className="w-[100px] h-[100px] object-cover rounded-lg" />
        <div className="flex-1 flex flex-col gap-1">
          <p className="font-medium text-base lg:text-[19px]">{roomTitle}</p>
          <p className="font-normal text-sm lg:text-[19px]">{address}</p>
        </div>
      </div>

      <div className="p-2 mb-1 border border-foreground/10 rounded-md gap-2 flex flex-col">
        <p className="text-sm font-bold">
          {t("book_location.booking_summary.first_section_subtitle")}
        </p>
        <div className="flex justify-between text-sm">
          <p>{t("book_location.booking_summary.dates")}</p>
          <p>{`${formatDate(startDate)} - ${formatDate(endDate)}`}</p>
        </div>

        <div className="flex justify-between text-sm">
          <p>{t("book_location.booking_summary.number_of_guests")}</p>
          <p>{numberOfGuests}</p>
        </div>
      </div>

      {showAdditionalServices && (
        <div className="mt-3 p-2 border border-foreground/10 rounded-md">
          <div className="text-sm font-bold">
            {t("book_location.booking_summary.second_section_subtitle")}
          </div>

          <p className="mb-4">
            <span className="text-foreground/60 text-sm font-normal leading-none">
              {t("book_location.booking_summary.services_description")}
            </span>
          </p>
          {displayedServices
            .filter((service) => !service.isPaymentCompulsory && service.serviceName !== "Internet")
            .map((service) => (
              <div key={service.id} className="flex items-start justify-between gap-4 py-2">
                <label className="flex items-center mr-[15px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isChecked(service.serviceName)}
                    onChange={() => handleCheckboxChange(service.serviceName)}
                    className="peer hidden"
                  />
                  <span className="w-5 h-5 flex items-center justify-center rounded-sm box-border transition-colors border border-foreground/10 peer-checked:bg-primary peer-checked:border-primary">
                    {isChecked(service.serviceName) && (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>

                  <span className="ml-2 text-sm leading-none">
                    {service.langNames[i18n.language] ?? service.serviceName}
                    {service.unitPrice === 0 ? (
                      <span className="text-foreground/60">
                        {" "}
                        | {t("book_location.booking_summary.free")}
                      </span>
                    ) : (
                      service.unitPrice && (
                        <span className="text-foreground/60">
                          {/* #2873: the unit suffix renders ONLY from a real
                              stored unit. Its absence used to be deliberate —
                              a blanket "pro Person" label was priced as
                              unitPrice × quantity and simply untrue — and a
                              service without a unit still gets the bare
                              price, because that is still exactly what it
                              costs. */}
                          {` | ${formatCurrency(service.unitPrice, i18n.language)}`}
                          {service.priceUnit
                            ? ` ${t(
                                "book_location.booking_summary.price_unit."
                                  + service.priceUnit
                              )} `
                            : " "}
                        </span>
                      )
                    )}
                  </span>
                </label>

                {isChecked(service.serviceName) && service.quantity === true && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDecrement(service.serviceName)}
                      className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-300 text-sm"
                    >
                      -
                    </button>
                    <div className="px-3 py-0.5 border border-gray-300 rounded-full text-center text-sm">
                      {quantityOf(service.serviceName)}
                    </div>
                    <button
                      onClick={() => handleIncrement(service.serviceName)}
                      className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-300 text-sm"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            ))}
          {additionalServices.length > 5 && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="text-primary cursor-pointer flex items-center mt-1 bg-transparent border-none p-0"
            >
              {showAll ? t("apartment.show_less") : t("apartment.show_more")}
              <KeyboardArrowDownIcon
                className={`transition-transform duration-300 ${
                  showAll ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>
          )}
        </div>
      )}

      {showVoucherInput && (
        <div className="flex flex-row justify-between items-center gap-4 my-2">
          <input
            type="text"
            name="coupon-input"
            aria-label={t("general.enter_coupon_code")}
            placeholder={t("general.enter_coupon_code")}
            onChange={(e) => setVoucherCode(e.target.value)}
            className=" px-3 h-10 py-2 w-full border text-foreground border-foreground/10 bg-background rounded-lg outline-none focus:border-primary placeholder:text-sm placeholder:leading-normal"
          />
          <CustomButton
            onClick={handleVoucherApply}
            loading={isCouponReedemptionLoading}
            disabled={isApplyButtonDisabled}
            spinnerColorClass="border-onPrimary border-t-primary"
            className="mt-2 bg-primary text-background"
          >
            {t("orders.ticket_overview.button")}
          </CustomButton>
        </div>
      )}

      <div className="p-2 mt-2 border border-foreground/10 rounded-md text-sm">
        <p className="mb-2 font-bold">
          {t("book_location.booking_summary.price_details")}
        </p>
        <div className="space-y-2">
          <PreviewNotice
            failed={previewFailed}
            pending={!preview && previewFetching}
          />
          {pricingClassName && (
            <p className="text-foreground/80">
              {t("book_location.booking_summary.price_for_class", { name: pricingClassName })}
            </p>
          )}
          {stayLines.map((line) => (
            <div className="flex justify-between" key={`${line.key}-${line.count}`}>
              <p>{stayLineLabel(t, line)}</p>
              <p>{formatCurrency(line.amount, i18n.language)}</p>
            </div>
          ))}
          {showAdditionalServices &&
            compulsoryLines.map((line) => (
              <div key={line.serviceId ?? line.serviceName} className="flex justify-between">
                <p>{line.langNames?.[i18n.language] ?? line.serviceName}</p>
                <p>{formatCurrency(line.amount, i18n.language)}</p>
              </div>
            ))}
          {showAdditionalServices && preview && (
            <div className="flex justify-between">
              <p>{t("book_location.booking_summary.additional_services")}</p>
              <p>{formatCurrency(additionalServicesTotal, i18n.language)}</p>
            </div>
          )}

          {reductionPrice > 0 && (
            <div className="flex justify-between">
              <p>{t("book_location.booking_summary.reduction")}</p>
              <p>-{formatCurrency(reductionPrice, i18n.language)}</p>
            </div>
          )}

          <div className="flex justify-between pt-3 font-medium border-t border-foreground/10">
            <p>{t("book_location.booking_summary.total")}</p>
            <p>{preview ? formatCurrency(totalPrice, i18n.language) : "–"}</p>
          </div>
          {isCovered && <CoveredCostCenterField />}
          {/* A covered booking still shows what the room costs — that is what
              the organization's agreement is worth, and it is the number the
              front desk quotes — but says plainly that nothing is due, so
              "total" is never read as a bill (#1926). */}
          {isCovered && (
            <p className="text-sm text-foreground/80">
              {coveringOrganizationName
                ? t("book_location.booking_summary.covered_by_organization", {
                    organization: coveringOrganizationName,
                  })
                : t("book_location.booking_summary.covered_no_payment")}
            </p>
          )}
          {/* Otherwise the tax line — unless the booking costs nothing, in
              which case there is no price for it to be a statement about. The
              total above stays visible either way. ADR-0008, amendments
              2026-09-14 and 2026-09-16. */}
          {showTaxLine && (
            <p className="text-sm text-foreground/80">
              {taxStatement.sentence}
            </p>
          )}
        </div>
      </div>

      {/* M10: what cancelling will cost, before the customer confirms. The
          amounts are shares of the total above, so they wait for the quote
          and are left out on a free booking. */}
      <CancellationTermsBlock
        rule={cancellationRule}
        covered={isCovered}
        start={quotedWindow?.fromDate ?? null}
        price={preview ? totalPrice : null}
        free={free}
        configuredFree={configuredFree}
        offerUntil={offerUntil}
        timeZone={timeZone}
      />

      <div className="mt-4">
        <button
          type="button"
          className="flex items-center cursor-pointer select-none bg-transparent border-none p-0 text-left"
          onClick={() => setRemarksOpen((open) => !open)}
        >
          <span className="text-foreground font-bold text-sm mr-1">
            {t("book_location.venue_reservation_overview.additional_remarks")}
          </span>
          <span className="text-foreground/60 text-sm font-normal mr-1">
            ({t("general.optional")})
          </span>
          <span className="text-foreground text-lg">
            {remarksOpen ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </span>
        </button>
        {remarksOpen && (
          <textarea
            className="w-full mt-2 p-2 border border-foreground/10 rounded-md resize-none min-h-[60px] text-foreground"
            aria-label={t("book_location.venue_reservation_overview.additional_remarks")}
            placeholder={t("general.remarks_placeholder")}
            value={comment}
            onChange={(e) => dispatch(setOrderComment(e.target.value))}
          />
        )}
      </div>
    </div>
  );
};

export default BookingSummaryShared;
